import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { 
  DollarSign, 
  Clock, 
  Truck, 
  AlertCircle,
  Calculator,
  HelpCircle,
  Shield,
  ChevronDown,
  ChevronUp,
  FileText,
  Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Driver Pay Explainer Page
 * 
 * PURPOSE: Plain-English explanation for drivers. NOT a contract.
 * VISIBILITY: Driver role only, hidden from navigation
 * ROUTE: /driver/pay-explainer
 * 
 * This page is linked from onboarding documents only.
 */
const DriverPayExplainerPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [explainerData, setExplainerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Allow drivers, admins, and super_admins to view
  const allowedRoles = ['driver', 'active_driver', 'applicant', 'driver_applicant', 'admin', 'super_admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const fetchExplainer = async () => {
      try {
        const response = await fetch(`${API_URL}/api/compensation/pay-explainer`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load pay explainer');
        }

        const data = await response.json();
        setExplainerData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchExplainer();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const data = explainerData;

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6" data-testid="pay-explainer-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-3">
            <DollarSign className="w-8 h-8 text-green-600" />
            {data.title}
          </h1>
          <p className="text-gray-600 mt-2">{data.subtitle}</p>
          
          {/* Notice Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-amber-800 font-medium">Important Notice</p>
              <p className="text-amber-700 text-sm">{data.notice}</p>
            </div>
          </div>
        </div>

        {/* Lease-On Section */}
        <section className="mb-8">
          <Card className="border-green-200">
            <CardHeader className="bg-green-50 border-b border-green-100">
              <CardTitle className="flex items-center gap-2 text-green-800">
                <Truck className="w-5 h-5" />
                {data.lease_on.title}
              </CardTitle>
              <CardDescription>Company authority drivers</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Summary Points */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Quick Summary</h4>
                <ul className="space-y-2">
                  {data.lease_on.summary.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Linehaul Pay */}
              <div className="border-b border-gray-100 pb-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  Linehaul Pay
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-2xl font-bold text-green-600 mb-1">
                      {data.lease_on.linehaul.rate}
                    </p>
                    <p className="text-sm text-gray-600">{data.lease_on.linehaul.what_counts}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <p className="text-sm text-gray-500 mb-1">What's NOT included:</p>
                    <p className="text-sm text-gray-700">{data.lease_on.linehaul.what_doesnt_count}</p>
                  </div>
                </div>
              </div>

              {/* Detention Pay */}
              <div className="border-b border-gray-100 pb-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  Detention Pay
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="font-medium text-gray-800">{data.lease_on.detention.when}</p>
                    <p className="text-xl font-bold text-blue-600 mt-1">{data.lease_on.detention.rate}</p>
                    <p className="text-sm text-gray-500 mt-1">{data.lease_on.detention.limit}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <p className="text-sm font-medium text-blue-800">How to claim:</p>
                    <p className="text-sm text-blue-700">{data.lease_on.detention.how_to_claim}</p>
                  </div>
                </div>
              </div>

              {/* Layover Pay */}
              <div className="border-b border-gray-100 pb-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-purple-600" />
                  Layover Pay
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">When it applies:</p>
                    <p className="text-gray-800">{data.lease_on.layover.when}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Rate:</p>
                    <p className="text-xl font-bold text-purple-600">{data.lease_on.layover.rate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Requirement:</p>
                    <p className="text-gray-800">{data.lease_on.layover.requirement}</p>
                  </div>
                </div>
              </div>

              {/* Breakdown Pay */}
              <div className="border-b border-gray-100 pb-6 mb-6">
                <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-orange-600" />
                  Breakdown Pay
                </h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">When it applies:</p>
                    <p className="text-gray-800">{data.lease_on.breakdown.when}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Rate:</p>
                    <p className="text-xl font-bold text-orange-600">{data.lease_on.breakdown.rate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Limit:</p>
                    <p className="text-gray-800">{data.lease_on.breakdown.limit}</p>
                  </div>
                </div>
              </div>

              {/* Example Calculation */}
              <div className="bg-green-50 rounded-lg p-4">
                <h4 className="font-semibold text-green-800 mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4" />
                  Example Calculation
                </h4>
                <p className="text-sm text-green-700 mb-3">{data.lease_on.example_calculation.scenario}</p>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Linehaul Pay:</span>
                    <span className="text-green-700">{data.lease_on.example_calculation.linehaul_pay}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Detention Pay:</span>
                    <span className="text-blue-700">{data.lease_on.example_calculation.detention_pay}</span>
                  </div>
                  <div className="flex justify-between border-t border-green-200 pt-2 font-bold">
                    <span className="text-gray-800">Total:</span>
                    <span className="text-green-800">{data.lease_on.example_calculation.total}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Dispatch-Only Section */}
        <section className="mb-8">
          <Card className="border-blue-200">
            <CardHeader className="bg-blue-50 border-b border-blue-100">
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <FileText className="w-5 h-5" />
                {data.dispatch_only.title}
              </CardTitle>
              <CardDescription>Contractors with their own authority</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Summary Points */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Quick Summary</h4>
                <ul className="space-y-2">
                  {data.dispatch_only.summary.map((item, index) => (
                    <li key={index} className="flex items-start gap-2 text-gray-700">
                      <span className="text-blue-600 font-bold mt-0.5">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Options */}
              <div className="grid md:grid-cols-2 gap-4">
                {data.dispatch_only.options.map((option, index) => (
                  <div key={index} className="border border-blue-100 rounded-lg p-4">
                    <h5 className="font-semibold text-blue-800">{option.name}</h5>
                    <p className="text-2xl font-bold text-blue-600 my-2">{option.rate}</p>
                    <p className="text-sm text-gray-600">{option.best_for}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ Section */}
        <section className="mb-8">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-gray-600" />
                Frequently Asked Questions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y divide-gray-100">
                {data.faq.map((item, index) => (
                  <div key={index} className="py-4">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="flex items-center justify-between w-full text-left"
                    >
                      <span className="font-medium text-gray-900">{item.question}</span>
                      {expandedFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-gray-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {expandedFaq === index && (
                      <p className="mt-2 text-gray-600 pl-4 border-l-2 border-blue-200">
                        {item.answer}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Compliance Notice */}
        <section>
          <Card className="border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-800">
                <Shield className="w-5 h-5" />
                {data.compliance_notice.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {data.compliance_notice.items.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-red-700">
                    <span className="text-red-600 font-bold mt-0.5">!</span>
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* Last Updated */}
        <div className="text-center text-sm text-gray-400 mt-8">
          Last updated: {data.last_updated}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DriverPayExplainerPage;
